import { Action } from "redux";
import { all, call, delay, fork, put, putResolve, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import {
    CLOSE_FORM,
    createCloseFormAction,
    createHideAllFormsAction,
    createHideFormOverlayAction,
    createHideLoadingIndicatorAction,
    createLoadHistoryFormsInfoAction,
    createLoadMenuItemsAction,
    createLoadNotificationGroupsAction,
    createLoadNotificationItemsAction,
    createLoadTaskResultDetailAction,
    createLogoutAction,
    createOpenFormAction,
    createSetGroupNotificationsAsReadAction,
    createSetNotificationItemAsReadAction,
    createShowFormOverlayAction,
    createShowLoadingIndicatorAction,
    createShowSettingsAction,
    createSwitchToFormAction,
    FORM_CLOSED,
    FORM_OVERLAY_CLICKED,
    FORM_PROFILE_CHANGED,
    FormOpenMethod,
    ICloseFormAction,
    IFormClosedAction,
    IFormOpenedAction,
    IFormOverlayClickedAction,
    IFormProfileChangedAction,
    IMenuItemsLoadedAction,
    INotificationGroupsLoadedAction,
    INotificationItemsLoadedAction,
    IOpenFormAction,
    ISwitchToFormAction,
    ITaskResultDetailLoadedAction,
    MENU_ITEMS_LOADED,
    NOTIFICATION_GROUPS_LOADED,
    NOTIFICATION_ITEMS_LOADED,
    OPEN_FORM,
    SWITCH_TO_FORM,
    TASK_RESULT_DETAIL_LOADED,
} from "@multiweb/reactCore/bridge";
import { createSetFetchingAction } from "@multiweb/reactCore/fetching";
import { createAreLocalizationKeysLoadedActionPattern, createLoadLocalizationKeysAction, hasLocalization } from "@multiweb/reactCore/i18n";
import { createToggleThemeAction } from "@multiweb/reactCore/theme";
import { forkIfExists, invariant, splitObject, SplitObjectFunction } from "@multiweb/reactUtils";
import { FormOpenMode, IMenuItemData, INotificationItem, NotificationFunctionType, NotificationGroupId, OpenFormData } from "@multiweb/types/contracts";
import {
    RequiredProp,
    SagaWithReturnType,
    SagaYieldReturn,
    TypedObject,
} from "@multiweb/types/react";
import { ProfileChangeType } from "@multiweb/uiShared/ProfileChangeType";
import {
    createAddProfileToRelationshipsAction,
    createClearLastSubmenuAction,
    createCloseSubmenusAction,
    createConcatNotificationItemsAction,
    createConcatPanelItemsAction,
    createIncreaseNotificationsPageCountAction,
    createRemoveProfileFormRelationshipsAction,
    createRemoveProfileFromFormsAction,
    createResetNotificationItemsAction,
    createResetNotificationsPageCountAction,
    createSetActiveSectionAction,
    createSetActiveSubmenuAction,
    createSetFormItemActionRestrictionsAction,
    createSetMenuFloatTransitionActiveAction,
    createSetMenuItemsAction,
    createSetMenuVisibilityAction,
    createSetNotificationGroupsAction,
    createSetNotificationGroupsPanelItemsAction,
    createSetProfileActionRestrictionsAction,
    createVisualAutomatProcessedAction,
} from "./actions";
import {
    FETCHING_KEY_FORM_INSTANCE_TITLE,
    FETCHING_KEY_FORM_ITEMS_LOCALIZATION,
    FETCHING_KEY_MENU_ITEMS,
    FETCHING_KEY_NOTIFICATION_GROUPS_LOCALIZATION,
    FETCHING_KEY_NOTIFICATION_ITEMS,
    FETCHING_KEY_STATIC_MENU_ITEMS_LOCALIZATION,
    LOAD_NEXT_NOTIFICATION_ITEMS_PAGE,
    NOTIFICATIONS_PAGE_SIZE,
    RELOAD_MENU,
    SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ,
    SET_MENU_ITEMS,
    SET_NOTIFICATION_GROUPS,
    SET_VISUAL_AUTOMAT_STATE,
    SHOW_GROUP_NOTIFICATIONS,
    TOGGLE_SUBMENU,
} from "./constants";
import { customActionsModule } from "./customActions";
import { favoriteItemsModule, loadFavoriteFormsSaga } from "./favoriteItems";
import { STATIC_LOC_KEYS } from "./locKeys";
import { logger } from "./logger";
import {
    createFocusableItemId,
    createSetFocusedItemAction,
    focusIfNotFocusedSaga,
    getNavigationNode,
    LOGOUT_BUTTON_FOCUSABLE_ID,
    MENU_EXPAND_BUTTON_FOCUSABLE_ID,
    MenuFocusableItemType,
    MenuNavigationSectionType,
    navigationModule,
    SETTINGS_BUTTON_FOCUSABLE_ID,
    TOGGLE_THEME_BUTTON_FOCUSABLE_ID,
    updateSelectedSectionNavigationSaga,
} from "./navigation";
import { getOpenedFormInfo, openedItemsModule } from "./openedItems";
import { MenuPanelItemId } from "./panelItems";
import { searchModule } from "./search";
import {
    allNotificationItemsLoaded,
    areFormItemWindowActionsEnabled,
    areProfileWindowActionsEnabled,
    getActiveSectionId,
    getActiveSubmenuIds,
    getAllowedRootFormItemIds,
    getFormItem,
    getFormItemByProfileId,
    getFormItemId,
    getFormItemsLocalizationKeys,
    getMenuItemParentId,
    getMenuVisibility,
    getNotificationGroup,
    getNotificationGroupsLocalizationKeys,
    getNotificationItem,
    getNotificationsStartIndex,
    getPanelItem,
    getPanelItemLastSubmenuId,
    getProfileFormRelationship,
    getProfileFormRelationshipModel,
    getVisualAutomatState,
    isCombinedItem,
    isEmptySection,
    isMenuVisible as isMenuVisibleSelector,
    isNotificationGroupPanelItemId,
    isSubmenuActive,
    shouldChangeActiveSection,
} from "./selectors";
import {
    IMenuItemDefaultActionAllowedAction,
    ISetVisualAutomatStateAction,
    IToggleSubmenuAction,
    MenuItemActivatedType,
    MenuVisibility,
    ProfileFormRelationship,
    VisualAutomatState,
} from "./types";
import {
    activationActionToMenuPanelItemId,
    createAllowedDefaultActionPattern,
    createFormContext,
    createItemIdForProfile,
    formOpenedAndShowInMenuActionPattern,
    getFormOpenMethodFromActivationAction,
    isCombinedItemFormPartActivatedAction,
    isOpenInTabEvent,
    menuItemsToPanelItems,
    normalizeNotificationGroups,
    notificationGroupsToPanelItems,
    transformNotificationItems,
} from "./utils";

export const saga: SagaWithReturnType = function* menuSaga() {
    yield fork(forkIfExists, customActionsModule.saga);
    yield fork(forkIfExists, navigationModule.saga);
    yield fork(forkIfExists, openedItemsModule.saga);
    yield fork(forkIfExists, searchModule.saga);
    yield fork(forkIfExists, favoriteItemsModule.saga);
    yield takeLatest(NOTIFICATION_GROUPS_LOADED, processLoadedNotificationGroupsSaga); // zpracování notifikačních skupin z loadMenuDataSaga i dalších následujících updatů
    yield fork(loadMenuDataSaga);

    yield takeLatest(OPEN_FORM, openFormPreProcessSaga);
    yield takeEvery(formOpenedAndShowInMenuActionPattern, formOpenedSaga);
    yield takeEvery(FORM_CLOSED, formClosedSaga);
    yield takeEvery(LOAD_NEXT_NOTIFICATION_ITEMS_PAGE, loadNextNotificationItemsPageSaga);
    yield takeEvery(SHOW_GROUP_NOTIFICATIONS, showGroupNotificationsSaga);
    yield takeEvery(SET_ACTIVE_GROUP_NOTIFICATIONS_AS_READ, setActiveGroupNotificationsAsRead);
    yield takeEvery(SET_MENU_ITEMS, setMenuItemsSaga);
    yield takeEvery(SET_NOTIFICATION_GROUPS, loadNotificationGroupsLocalizationSaga);
    const submenuItemDefaultActionAllowedActionPattern = createAllowedDefaultActionPattern(MenuItemActivatedType.formFolder);
    const expandButtonDefaultActionAllowedActionPattern = createAllowedDefaultActionPattern(MenuItemActivatedType.expandButton);
    const logoButtonDefaultActionAllowedActionPattern = createAllowedDefaultActionPattern(MenuItemActivatedType.logoButton);
    yield takeEvery([
        FORM_OVERLAY_CLICKED,
        logoButtonDefaultActionAllowedActionPattern,
        OPEN_FORM,
        SWITCH_TO_FORM,
        TOGGLE_SUBMENU,
        CLOSE_FORM,
        submenuItemDefaultActionAllowedActionPattern,
        expandButtonDefaultActionAllowedActionPattern,
    ], visualAutomatSaga);
    yield takeEvery(SET_VISUAL_AUTOMAT_STATE, setVisualAutomatStateSaga);
    yield takeEvery(FORM_PROFILE_CHANGED, formProfileChangedSaga);
    yield takeLatest(RELOAD_MENU, loadMenuDataSaga);
    // Ságy aktivovatelných položek s konkrétní vlastní akcí
    yield takeEvery([TOGGLE_SUBMENU, submenuItemDefaultActionAllowedActionPattern], toggleSubmenuSaga);
    // Ságy aktivovatelných položek s obecnou aktivační akcí
    const {
        form, panelItemForm, profile, openFormActionItem, openProfileActionItem, openFormInstanceActionItem, settingsButton, toggleThemeButton, logoutButton,
        openFormInTabActionItem, openProfileInTabActionItem, openFormInstanceInTabActionItem, closeActionItem,
        notificationItem, openedForm, panelItemCombinedItemForm,
    } = MenuItemActivatedType;
    yield takeEvery(expandButtonDefaultActionAllowedActionPattern, expandButtonActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(menuPanelItemActivationTypes), menuPanelItemSelectedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(settingsButton), settingsButtonActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(toggleThemeButton), toggleThemeButtonActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(logoutButton), logoutButtonActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(profile), profileActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern([form, panelItemForm, panelItemCombinedItemForm]), formItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openFormActionItem), openFormActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openProfileActionItem), openProfileActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openFormInstanceActionItem), openFormInstanceActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openFormInTabActionItem), openFormInTabActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openProfileInTabActionItem), openProfileInTabActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openFormInstanceInTabActionItem), openFormInstanceInTabActionItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(closeActionItem), closeActionItemActivatedSaga);
    yield takeEvery(logoButtonDefaultActionAllowedActionPattern, logoActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(notificationItem), notificationItemActivatedSaga);
    yield takeEvery(createAllowedDefaultActionPattern(openedForm), openedFormActivatedSaga);
};

/**
 * Typy aktivovatelných položek nacházejících se v menu panelu.
 */
const menuPanelItemActivationTypes = [
    MenuItemActivatedType.staticPanelItem,
    MenuItemActivatedType.panelItemForm,
    MenuItemActivatedType.panelItemFormFolder,
    MenuItemActivatedType.panelItemCombinedItemForm,
    MenuItemActivatedType.panelItemCombinedItemFolder,
    MenuItemActivatedType.notificationGroup,
    MenuItemActivatedType.expandButton,
];

/**
 * Sága zajišťuje aktulizaci vizuálních prvků - viditelnost překryvné vrstvy, menu a submenu.
 * @param action
 */
export const visualAutomatSaga: SagaWithReturnType<[Action]> = function* visualAutomatSaga(action) {
    const menuPanelItemId = action instanceof IMenuItemDefaultActionAllowedAction ? activationActionToMenuPanelItemId(action) : undefined;
    const activeSectionId: SagaYieldReturn<typeof getActiveSectionId> = yield select(getActiveSectionId);
    const sameMenuPanelItemSelected = menuPanelItemId === activeSectionId;
    const isExpandButtonClickedAction = action instanceof IMenuItemDefaultActionAllowedAction && action.payload.itemType === MenuItemActivatedType.expandButton;
    const isLogoClickedAction = action instanceof IMenuItemDefaultActionAllowedAction && action.payload.itemType === MenuItemActivatedType.logoButton;
    const isFormOverlayClickedAction = action instanceof IFormOverlayClickedAction;
    const isOpenFormAction = action instanceof IOpenFormAction && action.payload.openMethod !== FormOpenMethod.inTab;
    const isSwitchToFormAction = action instanceof ISwitchToFormAction;
    const isToggleSubmenuAction = action instanceof IToggleSubmenuAction;
    const isSubmenuItemDefaultAction = action instanceof IMenuItemDefaultActionAllowedAction && action.payload.itemType === MenuItemActivatedType.formFolder;
    const isCloseFormAction = action instanceof ICloseFormAction;
    const sameRootSubmenuToggled = action instanceof IToggleSubmenuAction && (yield select(getActiveSubmenuIds))[0] === action.payload.submenuId;
    const emptySectionSelected = !!menuPanelItemId && ((yield select(isEmptySection, menuPanelItemId)) || isCombinedItemFormPartActivatedAction(action));
    const notEmptySectionSelected = !!menuPanelItemId && !(yield select(isEmptySection, menuPanelItemId)) && !isCombinedItemFormPartActivatedAction(action);
    const differentMenuPanelItemSelected = !!menuPanelItemId && menuPanelItemId !== activeSectionId;
    const hasMenuPanelItemLastSubmenu = menuPanelItemId ? !!(yield select(getPanelItemLastSubmenuId, menuPanelItemId)) : false;
    const actualVisualAutomatState: SagaYieldReturn<typeof getVisualAutomatState> = yield select(getVisualAutomatState);

    /**
     * Stavy viz {@link VisualAutomatState}
     */
    const visualAutomat: TypedObject<TypedObject<boolean>> = {
        "000": {
            "020": isExpandButtonClickedAction,
            "110": notEmptySectionSelected && !hasMenuPanelItemLastSubmenu,
            "111": notEmptySectionSelected && hasMenuPanelItemLastSubmenu,
        },
        "020": {
            "000": isExpandButtonClickedAction,
            "121": isToggleSubmenuAction || isSubmenuItemDefaultAction || hasMenuPanelItemLastSubmenu,
        },
        "121": {
            "000": isExpandButtonClickedAction,
            "020": sameRootSubmenuToggled || isFormOverlayClickedAction || isLogoClickedAction || isOpenFormAction || isCloseFormAction
                || (differentMenuPanelItemSelected && notEmptySectionSelected && !hasMenuPanelItemLastSubmenu),
        },
        "110": {
            "000" : sameMenuPanelItemSelected || isFormOverlayClickedAction || isLogoClickedAction || isOpenFormAction
                || isSwitchToFormAction || differentMenuPanelItemSelected && emptySectionSelected,
            "020": isExpandButtonClickedAction,
            // Přechod otevření submenu je řešen v toggleSubmenuSaga změnou aktivního submenu
        },
        "111": {
            "000": sameMenuPanelItemSelected || isFormOverlayClickedAction || isLogoClickedAction || isOpenFormAction
                || isSwitchToFormAction || differentMenuPanelItemSelected && emptySectionSelected || isCloseFormAction,
            "110": sameRootSubmenuToggled || (differentMenuPanelItemSelected && notEmptySectionSelected && !hasMenuPanelItemLastSubmenu),
            "121": isExpandButtonClickedAction,
        },
    };

    if (actualVisualAutomatState) {
        const stepConditionResult = Object.entries(visualAutomat[actualVisualAutomatState]).find(([, condition]) => condition);
        if (stepConditionResult) {
            const nextState = stepConditionResult[0];
            // Kontrola, zda menu přechází z překrývajícího módu do skrytého nebo naopak
            const menuFloatTransitionActive = actualVisualAutomatState[1] === "1" && nextState[1] === "0"
                || actualVisualAutomatState[1] === "0" && nextState[1] === "1";
            yield put(createSetMenuFloatTransitionActiveAction(menuFloatTransitionActive));
            yield call(visualAutomatStepSaga, nextState as VisualAutomatState);
        }
    }
    yield put(createVisualAutomatProcessedAction());
};

/**
 * Zpracovává požadavky na přechod do konkrétního stavu vizuálního automatu. Po dokončení vysílá akci o dokončení aktualizace.
 * @param action Akce obsahující nový požadovaný stav automatu.
 */
export const setVisualAutomatStateSaga: SagaWithReturnType<[ISetVisualAutomatStateAction]> = function* setVisualAutomatStateSaga(action) {
    yield call(visualAutomatStepSaga, action.payload.state);
    yield put(createVisualAutomatProcessedAction());
};

/**
 * Sága provádí přechody mezi stavy automatu zajišťujícího aktualizaci viditelnosti překryvné vrstvy, menu a submenu.
 * @param targetState Cílový stav automatu
 */
export const visualAutomatStepSaga: SagaWithReturnType<[VisualAutomatState]> = function* visualAutomatStepSaga(targetState) {
    switch (targetState) {
        case "000": {
            yield put(createHideFormOverlayAction());
            yield put(createSetMenuVisibilityAction(MenuVisibility.hidden));
            yield put(createCloseSubmenusAction());
            break;
        }
        case "020": {
            yield put(createHideFormOverlayAction());
            yield put(createSetMenuVisibilityAction(MenuVisibility.expanded));
            yield put(createCloseSubmenusAction());
            break;
        }
        case "121": {
            yield put(createShowFormOverlayAction());
            yield put(createSetMenuVisibilityAction(MenuVisibility.expanded));
            // Viditelnost submenu je řešena existencí identifikátoru submenu
            break;
        }
        case "110": {
            yield put(createShowFormOverlayAction());
            yield put(createSetMenuVisibilityAction(MenuVisibility.floated));
            yield put(createCloseSubmenusAction());
            break;
        }
        case "111": {
            yield put(createShowFormOverlayAction());
            yield put(createSetMenuVisibilityAction(MenuVisibility.floated));
            // Viditelnost submenu je řešena existencí identifikátoru submenu
            break;
        }
    }
};

/**
 * Zajišťuje posloupnost volání po vybrání položky panelu menu.
 * Jednotlivé ságy na sebe navazují.
 * @param action Akce aktivace položky menu panelu reprezentující sekci. Předpokládá, že aktivace odlišných typů položek je již odfiltrována.
 */
export const menuPanelItemSelectedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* menuPanelItemSelectedSaga(action) {
    const menuPanelItemId: SagaYieldReturn<typeof activationActionToMenuPanelItemId> = yield call(activationActionToMenuPanelItemId, action);
    if (!menuPanelItemId) {
        return;
    }
    // Aktualizace viditelnosti musí proběhnout před nastavením nové sekce, protože automat potřebuje předchozí vybranou sekci
    yield call(visualAutomatSaga, action);
    // Odstranění notifikačních dat před změnou sekce
    yield call(resetNotificationsSaga, action);
    // Vybrání sekce při neprázdné složce
    yield call(selectSectionSaga, menuPanelItemId);
    // Aktualizace navigace
    yield call(updateSelectedSectionNavigationSaga, menuPanelItemId);
    // Znovu otevření submenu, které bylo pro dané menuPanelItemId otevřené naposledy
    yield call(activateLastSubmenuIfNeededSaga, menuPanelItemId);
};

/**
 * Součást ošetření kliku na položku menu panelu.
 * Pokud jde o aktivující klik a pokud při poslední situaci, kdy tato položka byla otevřená, bylo otevřené i její podmenu,
 * pak toto podmenu znovu otevře.
 *
 * @param menuPanelItemId Identifikátor položky panelu menu
 */
export const activateLastSubmenuIfNeededSaga: SagaWithReturnType<[string]> = function* activateLastSubmenuIfNeededSaga(menuPanelItemId) {
    const lastSubmenuId: SagaYieldReturn<typeof getPanelItemLastSubmenuId> = yield select(getPanelItemLastSubmenuId, menuPanelItemId);
    const menuVisibility: SagaYieldReturn<typeof getMenuVisibility> = yield select(getMenuVisibility);
    if (lastSubmenuId && menuVisibility !== MenuVisibility.hidden) {
        yield call(openSubmenuSaga, lastSubmenuId, menuPanelItemId);
    }
};

/**
 * Sdružuje načítání potřebných dat pro menu a řeší zobrazení indikátoru načítání.
 */
export const loadMenuDataSaga: SagaWithReturnType = function* loadMenuDataSaga() {
    yield put(createShowLoadingIndicatorAction());
    yield fork(hideInitialLoadingIndicatorSaga);
    yield fork(loadHistoryItemsSaga);
    yield fork(loadMenuItemsSaga);
    yield fork(loadStaticMenuItemsLocalizationSaga);
    yield fork(loadNotificationGroupsSaga);
};

/**
 * Obsluhuje skrytí indikátoru načítání po iniciálním načítání dat.
 */
export const hideInitialLoadingIndicatorSaga: SagaWithReturnType = function* hideInitialLoadingIndicatorSaga() {
    yield all([
        take(createAreLocalizationKeysLoadedActionPattern(FETCHING_KEY_FORM_ITEMS_LOCALIZATION)),
        take(NOTIFICATION_GROUPS_LOADED),
    ]);
    yield put(createHideLoadingIndicatorAction());
};

/** Obsluhuje načtení lokalizace pro statické položky menu. */
export const loadStaticMenuItemsLocalizationSaga: SagaWithReturnType = function* loadStaticMenuItemsLocalizationSaga() {
    yield put(createLoadLocalizationKeysAction(STATIC_LOC_KEYS, FETCHING_KEY_STATIC_MENU_ITEMS_LOCALIZATION));
};

/** Obsluhuje akce po uložení položek menu do stavu. */
export const setMenuItemsSaga: SagaWithReturnType = function* setMenuItemsSaga() {
    yield fork(loadFavoriteFormsSaga);
    yield call(loadFormItemsLocalizationSaga);
};

/**
 * Obsluhuje načtení položek pro menu.
 */
export const loadMenuItemsSaga: SagaWithReturnType = function* loadMenuItemsSaga() {
    yield put(createSetFetchingAction(FETCHING_KEY_MENU_ITEMS, true));
    yield put(createLoadMenuItemsAction());
    const menuItemsLoadedAction: IMenuItemsLoadedAction = yield take(MENU_ITEMS_LOADED);
    const { menuItems } = menuItemsLoadedAction.payload;
    yield put(createSetMenuItemsAction(menuItems));
    // Získání kořenových formulářových položek pro použití v panelu menu
    const [rootMenuItems] = yield call<SplitObjectFunction<IMenuItemData>>(splitObject, menuItems.items, (id: string) => menuItems.rootFormItemIds.includes(+id));
    yield put(createConcatPanelItemsAction(menuItemsToPanelItems(rootMenuItems)));
    yield put(createSetFetchingAction(FETCHING_KEY_MENU_ITEMS, false));
};

/**
 * Obsluhuje načtení lokalizace pro formulářové položky menu.
 */
export const loadFormItemsLocalizationSaga: SagaWithReturnType = function* loadFormItemsLocalizationSaga() {
    const formItemsLocalizationKeys: SagaYieldReturn<typeof getFormItemsLocalizationKeys> = yield select(getFormItemsLocalizationKeys);
    yield put(createLoadLocalizationKeysAction(formItemsLocalizationKeys, FETCHING_KEY_FORM_ITEMS_LOCALIZATION));
};

/**
 * Obsluhuje načítání historicky otevřených položek. Transformaci položek provádí historyItemsUpdatedSaga.
 */
export const loadHistoryItemsSaga: SagaWithReturnType = function* loadHistoryItemsSaga() {
    yield put(createLoadHistoryFormsInfoAction());
};

/**
 * Obsluhuje zpracování side effectů po otevření formuláře.
 * @param action
 */
export const formOpenedSaga: SagaWithReturnType<[IFormOpenedAction]> = function* formOpenedSaga(action) {
    const { openedFormInfo, hideWindowActions } = action.payload;
    const { menuItemId, profileId, titleLocKey } = openedFormInfo;
    if (titleLocKey) {
        const titleLocalizationLoaded = yield select(hasLocalization, titleLocKey);
        if (!titleLocalizationLoaded) {
            yield put(createLoadLocalizationKeysAction([titleLocKey], `${FETCHING_KEY_FORM_INSTANCE_TITLE}/${titleLocKey}`));
        }
    }
    if (profileId && menuItemId) {
        yield put(createSetProfileActionRestrictionsAction(profileId, hideWindowActions));
    } else if (menuItemId) {
        yield put(createSetFormItemActionRestrictionsAction(menuItemId, hideWindowActions));
    }
};

/**
 * Obsluhuje zpracování side effectů po zavření formuláře.
 * @param action
 */
export const formClosedSaga: SagaWithReturnType<[IFormClosedAction]> = function* formClosedSaga(action) {
    const { closedFormInfo } = action.payload;
    const { menuItemId, profileId } = closedFormInfo;
    if (profileId && menuItemId) {
        yield put(createSetProfileActionRestrictionsAction(profileId, false));
    } else if (menuItemId) {
        yield put(createSetFormItemActionRestrictionsAction(menuItemId, false));
    }
};

/**
 * Resetuje stav notifikačních položek
 * @param action Akce aktivace položky.
 */
export const resetNotificationsSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* resetNotificationsSaga(action) {
    const isMenuVisible: SagaYieldReturn<typeof isMenuVisibleSelector> = yield select(isMenuVisibleSelector);
    if (action.payload.itemType === MenuItemActivatedType.notificationGroup && isMenuVisible) {
        yield put(createResetNotificationItemsAction());
        yield put(createResetNotificationsPageCountAction());
    }
};

/**
 * Obsluhuje vybrání sekce po vybrání položky panelu menu
 * @param menuPanelItemId Identifikátor položky panelu menu
 */
export const selectSectionSaga: SagaWithReturnType<[string]> = function* selectSectionSaga(menuPanelItemId) {
    if (yield select(shouldChangeActiveSection, menuPanelItemId)) {
        yield put(createSetActiveSectionAction(menuPanelItemId));
    }
};

/**
 * Vrací Id rodičovského "submenu" nebo `undefined`, pokud `submenuId` představuje hlavní submenu.
 * @param submenuId
 */
export const getParentSubmenuIdSaga: SagaWithReturnType<[number], number | undefined> = function* getParentSubmenuIdSaga(submenuId) {
    const parentSubmenuId: SagaYieldReturn<typeof getMenuItemParentId> = yield select(getMenuItemParentId, submenuId);
    invariant(!!parentSubmenuId, "parentSubmenuId should not be null");
    const allowedRootFormItemIds: SagaYieldReturn<typeof getAllowedRootFormItemIds> = yield select(getAllowedRootFormItemIds);
    return !allowedRootFormItemIds.includes(parentSubmenuId) ? parentSubmenuId : undefined;
};

const shouldClearLastSubmenuOnClose = (action: IToggleSubmenuAction | IMenuItemDefaultActionAllowedAction) => {
    return action instanceof IToggleSubmenuAction;
};

/**
 * Obsluhuje zpracování interakce se submenu. Na základě aktuální stavu otevře submenu.
 */
export const toggleSubmenuSaga: SagaWithReturnType<[IToggleSubmenuAction | IMenuItemDefaultActionAllowedAction]> = function* toggleSubmenuSaga(action) {
    let submenuId;
    if (action instanceof IToggleSubmenuAction) {
        submenuId = action.payload.submenuId;
    } else {
        submenuId = action.payload.itemId as number;
    }
    const activeSubmenuSelected: SagaYieldReturn<typeof isSubmenuActive> = yield select(isSubmenuActive, submenuId);
    const submenuFocusableId: SagaYieldReturn<typeof createFocusableItemId> = yield call(createFocusableItemId, submenuId, MenuFocusableItemType.folderItem, MenuNavigationSectionType.forms);
    const activeSectionId: SagaYieldReturn<typeof getActiveSectionId> = yield select(getActiveSectionId);

    if (activeSubmenuSelected) {
        // Podmenu je již aktivní -> zavře se. A případně se aktivuje nadřazené "podmenu".

        const parentSubmenuId: SagaYieldReturn<typeof getParentSubmenuIdSaga> = yield call(getParentSubmenuIdSaga, submenuId);
        if (parentSubmenuId) { // existuje nadřazené "podmenu" -> aktivuje se
            yield put(createSetActiveSubmenuAction(parentSubmenuId, activeSectionId));
        } else if (shouldClearLastSubmenuOnClose(action)) { // žádné nadřazené "podmenu" není
            yield put(createClearLastSubmenuAction(activeSectionId));
        }
        yield put(createSetFocusedItemAction(submenuFocusableId));
    } else {
        yield call(openSubmenuSaga, submenuId, activeSectionId);
    }
};

/**
 * Aktivuje zadané podmenu a zaměří jeho první položku.
 * (Určeno k otevření nového podmenu, které doposud otevřené není.)
 * @param submenuId Id podmenu, které má být aktivováno.
 * @param menuPanelItemId Id položky na hlavním panelu, v rámci které má být aktivováno dané podmenu.
 */
export const openSubmenuSaga: SagaWithReturnType<[number, string]> = function* openSubmenuSaga(submenuId, menuPanelItemId) {
    yield put(createSetActiveSubmenuAction(submenuId, menuPanelItemId));
    // nastaví zaměření na první položku z nově otevřeného submenu
    const submenuFocusableId: SagaYieldReturn<typeof createFocusableItemId> = yield call(createFocusableItemId, submenuId, MenuFocusableItemType.folderItem, MenuNavigationSectionType.forms);
    const submenuNode: SagaYieldReturn<typeof getNavigationNode> = yield select(getNavigationNode, submenuFocusableId);
    if (submenuNode && submenuNode.rightId) {
        yield put(createSetFocusedItemAction(submenuNode.rightId));
    }
};

/**
 * Obsluhuje zobrazení formuláře s přehledem notifikací aktivní notifikační skupiny.
 */
export const showGroupNotificationsSaga: SagaWithReturnType = function* showGroupNotificationsSaga() {
    const activeSectionId: SagaYieldReturn<typeof getActiveSectionId> = yield select(getActiveSectionId);

    if (!(yield select(isNotificationGroupPanelItemId, activeSectionId))) {
        yield call(logger.warn, "The notification section is not active!");
        return;
    }

    const panelItem: SagaYieldReturn<typeof getPanelItem> = yield select(getPanelItem, activeSectionId);
    if (!panelItem) {
        yield call(logger.warn, "The panel item doesn't exist!");
        return;
    }

    const notificationGroup: SagaYieldReturn<typeof getNotificationGroup> = yield select(getNotificationGroup, panelItem.itemId as NotificationGroupId);
    if (!notificationGroup) {
        yield call(logger.warn, "The notification group identifier %d is not valid!", panelItem.itemId);
        return;
    }

    const { formCode } = notificationGroup;
    yield put(createOpenFormAction(formCode, FormOpenMethod.newOrExisting, { openMode: FormOpenMode.form }));
};

/**
 * Obsluhuje označení všech notifikací aktivní skupiny za přečtené.
 */
export const setActiveGroupNotificationsAsRead: SagaWithReturnType = function* setGroupNotificationsAsRead() {
    const activeSectionId: SagaYieldReturn<typeof getActiveSectionId> = yield select(getActiveSectionId);

    if (!(yield select(isNotificationGroupPanelItemId, activeSectionId))) {
        yield call(logger.warn, "The notification section is not active!");
        return;
    }

    const panelItem: SagaYieldReturn<typeof getPanelItem> = yield select(getPanelItem, activeSectionId);
    if (!panelItem) {
        yield call(logger.warn, "The panel item doesn't exist!");
        return;
    }

    const notificationGroup: SagaYieldReturn<typeof getNotificationGroup> = yield select(getNotificationGroup, panelItem.itemId as NotificationGroupId);
    if (!notificationGroup) {
        yield call(logger.warn, "The notification group identifier %d is not valid!", panelItem.itemId);
        return;
    }

    yield putResolve(createSetGroupNotificationsAsReadAction(notificationGroup.id as NotificationGroupId, panelItem.id));
};

/**
 * Obsluhuje načtení další stránky notifikačních položek. Zajišťuje rušení požadavku na načtení, pokud již byly načteny všechny položky.
 */
export const loadNextNotificationItemsPageSaga: SagaWithReturnType = function* loadNextNotificationItemsPageSaga() {
    if (yield select(allNotificationItemsLoaded)) {
        return;
    }

    yield fork(loadGroupNotificationsSaga);
};

/**
 * Obsluhuje otevření formuláře s výsledkem úlohy.
 * @param objId Identifikátor otevíraného objektu.
 * @param openMethod Metoda otevření.
 */
export const openTaskResultFormSaga: SagaWithReturnType<[number, FormOpenMethod]> = function* openTaskResultFormSaga(objId, openMethod) {
    yield put(createLoadTaskResultDetailAction(objId));
    const taskResultDetailLoadedAction: ITaskResultDetailLoadedAction = yield take(TASK_RESULT_DETAIL_LOADED);
    const { formCode, objectIds } = taskResultDetailLoadedAction.payload.taskResultDetail.extraParameters;
    const { resultMessages } = taskResultDetailLoadedAction.payload.taskResultDetail;

    if (!formCode) {
        yield call(logger.error, "Unable to open detail form. Task result does not contain all extra information.");
        return;
    }
    const condition = objectIds && objectIds.length > 0
        ? `ID IN [${objectIds.join()}]`
        : undefined;
    yield put(createOpenFormAction(formCode, openMethod, {
        condition,
        openMode: FormOpenMode.form,
        resultMessages,
    }));
};

/**
 * Obsluhuje otevření formuláře notifikační položky.
 * @param formCode Kód formuláře k otevření
 * @param openMethod Metoda otevření
 * @param selectedId Identifikátor otevíraného objektu.
 */
export const openNotificationItemFormSaga: SagaWithReturnType<[string, FormOpenMethod, string?]> = function* openNotificationItemFormSaga(formCode, openMethod, selectedId?) {
    const formId: SagaYieldReturn<typeof getFormItemId> = yield select(getFormItemId, formCode);

    if (!formId) {
        yield call(logger.warn, "There is no form identifier for a form code %s", formCode);
        return;
    }

    const formItem: SagaYieldReturn<typeof getFormItem> = yield select(getFormItem, formId);
    if (!formItem) {
        yield call(logger.warn, "There is no form item for a form id %d", formId);
        return;
    }

    const { openMode } = formItem;
    yield put(createOpenFormAction(formCode, openMethod, { selectedId, openMode }));
};

/**
 * Obsluhuje vybrání notifikační položky. Výsledkem je otevření formuláře.
 * @param action
 */
export const notificationItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* notificationItemActivatedSaga(action) {
    const { itemId, mouseEvent } = action.payload;
    const notificationItem: SagaYieldReturn<typeof getNotificationItem> = yield select(getNotificationItem, itemId as number);
    if (!notificationItem) {
        yield call(logger.warn, "The notification item is not valid!");
        return;
    }

    const formOpenMethod = mouseEvent && (yield call(isOpenInTabEvent, mouseEvent)) ? FormOpenMethod.inTab : FormOpenMethod.new;

    const { id, formCode, objId, functionType }: INotificationItem = notificationItem;
    if (functionType === NotificationFunctionType.taskResult) {
        if (!objId) {
            yield call(logger.warn, "The object ID is not valid!");
            return;
        }
        yield fork(openTaskResultFormSaga, objId, formOpenMethod);
    } else if (formCode) {
        yield fork(openNotificationItemFormSaga, formCode, formOpenMethod, objId?.toString());
    }

    yield put(createSetNotificationItemAsReadAction(id, true));
};

/**
 * Obsluhuje načtení notifikačních položek aktivní notifikační skupiny.
 */
export const loadGroupNotificationsSaga: SagaWithReturnType = function* loadGroupNotificationsSaga() {
    const notificationsStartIndex: SagaYieldReturn<typeof getNotificationsStartIndex> = yield select(getNotificationsStartIndex);
    const activeSectionId: SagaYieldReturn<typeof getActiveSectionId> = yield select(getActiveSectionId);

    if (!(yield select(isNotificationGroupPanelItemId, activeSectionId))) {
        yield call(logger.warn, "An active section is not a notification section. Active section ID is %s!", activeSectionId);
        return;
    }

    const panelItem: SagaYieldReturn<typeof getPanelItem> = yield select(getPanelItem, activeSectionId);
    if (!panelItem) {
        yield call(logger.warn, "The panel item doesn't exist!");
        return;
    }

    const notificationGroupId = panelItem.itemId;
    if (!notificationGroupId) {
        yield call(logger.warn, "The notification group identifier %d is not valid!", notificationGroupId);
        return;
    }

    yield put(createSetFetchingAction(FETCHING_KEY_NOTIFICATION_ITEMS, true));
    yield put(createLoadNotificationItemsAction(notificationGroupId as NotificationGroupId, notificationsStartIndex, NOTIFICATIONS_PAGE_SIZE));
    const { payload }: INotificationItemsLoadedAction = yield take(NOTIFICATION_ITEMS_LOADED);
    if (payload.notificationGroupId === notificationGroupId) {
        yield put(createConcatNotificationItemsAction(transformNotificationItems(payload.notificationItems)));
        yield put(createIncreaseNotificationsPageCountAction());
    }
    yield put(createSetFetchingAction(FETCHING_KEY_NOTIFICATION_ITEMS, false));
};

/**
 * Obsluhuje načtení notifikačních skupin. Zpracování načtených dat zajišťuje {@link processLoadedNotificationGroupsSaga}, která se spouští jak pro prvotní načtení tak pro následující aktualizace.
 */
export const loadNotificationGroupsSaga: SagaWithReturnType = function* loadNotificationGroupsSaga() {
    yield put(createLoadNotificationGroupsAction());
};

/**
 * Zajišťuje zpracování načtených notifikačních skupin z dat akce {@link NOTIFICATION_GROUPS_LOADED}.
 * @param notificationGroupsLoadedAction
 */
export const processLoadedNotificationGroupsSaga: SagaWithReturnType<[INotificationGroupsLoadedAction]> = function* processLoadedNotificationGroupsSaga(notificationGroupsLoadedAction) {
    const normalizedNotificationGroups: SagaYieldReturn<typeof normalizeNotificationGroups> = yield call(normalizeNotificationGroups, notificationGroupsLoadedAction.payload.notificationGroups);
    yield put(createSetNotificationGroupsAction(normalizedNotificationGroups));
    yield put(createSetNotificationGroupsPanelItemsAction(notificationGroupsToPanelItems(normalizedNotificationGroups)));
};

/**
 * Obsluhuje načtení lokalizace pro notifikační skupiny.
 */
export const loadNotificationGroupsLocalizationSaga: SagaWithReturnType = function* loadNotificationGroupsLocalizationSaga() {
    const notificationGroupsLocalizationKeys: SagaYieldReturn<typeof getNotificationGroupsLocalizationKeys> = yield select(getNotificationGroupsLocalizationKeys);
    yield put(createLoadLocalizationKeysAction(notificationGroupsLocalizationKeys, FETCHING_KEY_NOTIFICATION_GROUPS_LOCALIZATION));
};

/**
 * Obsluhuje akci kliknutí na logo aplikace. Aktualizuje zaměřený prvek ve stavu a skryje otevřené formuláře.
 */
export const logoActivatedSaga: SagaWithReturnType = function* logoActivatedSaga() {
    const logoFocusableId: SagaYieldReturn<typeof createFocusableItemId> = yield call(createFocusableItemId, MenuPanelItemId.logo, MenuFocusableItemType.panelItem, MenuNavigationSectionType.static);
    yield put(createSetFocusedItemAction(logoFocusableId));
    yield put(createHideAllFormsAction());
};

/**
 * Obsluhuje akci kliknutí na tlačítko rozbalení/sbalení menu. Aktualizuje zaměřený prvek ve stavu.
 */
export const expandButtonActivatedSaga: SagaWithReturnType = function* expandButtonActivatedSaga() {
    yield put(createSetFocusedItemAction(MENU_EXPAND_BUTTON_FOCUSABLE_ID));
};

/**
 * Obsluhuje akci kliknutí na tlačítko pro zobrazení dialogu s nastavením.
 */
export const settingsButtonActivatedSaga: SagaWithReturnType = function* settingsButtonActivatedSaga() {
    yield put(createShowSettingsAction());
    yield fork(focusIfNotFocusedSaga, SETTINGS_BUTTON_FOCUSABLE_ID);
};

/**
 * Obsluhuje akci kliknutí na tlačítko pro změnu tématu.
 */
export const toggleThemeButtonActivatedSaga: SagaWithReturnType = function* toggleThemeButtonClickedSaga() {
    yield put(createToggleThemeAction());
    yield fork(focusIfNotFocusedSaga, TOGGLE_THEME_BUTTON_FOCUSABLE_ID);
};

/**
 * Obsluhuje akci kliknutí na tlačítko pro odhlášení uživatele.
 */
export const logoutButtonActivatedSaga: SagaWithReturnType = function* logoutButtonActivatedSaga() {
    yield put(createLogoutAction());
    yield fork(focusIfNotFocusedSaga, LOGOUT_BUTTON_FOCUSABLE_ID);
};

/**
 * Obsluhuje side effecty při žádosti o otevření formuláře.
 */
export const openFormPreProcessSaga: SagaWithReturnType<[IOpenFormAction]> = function* openFormPreProcessSaga(action) {
    if (action.payload.openMethod === FormOpenMethod.inTab) {
        return;
    }

    const menuItemId = action.payload.openFormData?.menuItemId;
    const isCombinedItemType = menuItemId && (yield select(isCombinedItem, menuItemId));
    if (!isCombinedItemType) { // pro kombinované položky se nepřepíná sekce, protože se zároveň otvírá menu s podřízenými položkami
        yield put(createSetActiveSectionAction(MenuPanelItemId.quickAccess));
    }
};

/**
 * Obsluhuje výchozí chování pro položku profilu.
 * @param action
 */
export const profileActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* profileActivatedSaga(action) {
    const profileFormRelationship: SagaYieldReturn<typeof getProfileFormRelationship> = yield select(getProfileFormRelationship, action.payload.itemId as string);
    if (profileFormRelationship) {
        const formOpenMethod: SagaYieldReturn<typeof getFormOpenMethodFromActivationAction> = yield call(getFormOpenMethodFromActivationAction, action);
        yield call(openProfileSaga, profileFormRelationship.profileId, formOpenMethod, profileFormRelationship.formItemId);
    } else {
        yield call(logger.warn, "Cannot open profile because profileId for itemId %s was not found.", action.payload.itemId);
    }
};

/**
 * Obsluhuje výchozí chování pro položku formuláře.
 * @param action
 */
export const formItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* formItemActivatedSaga(action) {
    if (action.payload.preventFormDefault)
        return;

    const formItemId = action.payload.itemId as number;
    const formOpenMethod: SagaYieldReturn<typeof getFormOpenMethodFromActivationAction> = yield call(getFormOpenMethodFromActivationAction, action);
    yield call(openFormItemSaga, formItemId, formOpenMethod);
};

/**
 * Obsluhuje aktivaci otevřených položek (formulářů s položkou v menu, profilů, formulářů bez položky v menu).
 * @param action
 */
export const openedFormActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openedFormActivatedSaga(action) {
    const { itemId, mouseEvent } = action.payload;
    const formInstanceId = itemId as string;
    const isOpenInTab = !!mouseEvent && (yield call(isOpenInTabEvent, mouseEvent));
    if (isOpenInTab) {
        yield call(openFormInstanceSaga, formInstanceId, FormOpenMethod.inTab);
    } else {
        yield put(createSwitchToFormAction(formInstanceId));
    }
};

/**
 * Obsluhuje výchozí chování pro akční položku otevřít ve formulářových položkách menu.
 * @param action
 */
export const openFormActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openFormActionItemActivatedSaga(action) {
    const formItemId = action.payload.itemId as number;
    yield call(openFormItemSaga, formItemId, FormOpenMethod.new);
};

/**
 * Obsluhuje výchozí chování pro akční položku otevřít v profilových položkách menu.
 * @param action
 */
export const openProfileActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openProfileActionItemActivatedSaga(action) {
    const profileFormRelationship: SagaYieldReturn<typeof getProfileFormRelationship> = yield select(getProfileFormRelationship, action.payload.itemId as string);
    if (profileFormRelationship) {
        yield call(openProfileSaga, profileFormRelationship.profileId, FormOpenMethod.new, profileFormRelationship.formItemId);
    } else {
        yield call(logger.warn, "Cannot open profile because profileId for itemId %s was not found.", action.payload.itemId);
    }
};

/**
 * Obsluhuje výchozí chování pro akční položku otevřít v otevřených položkách menu.
 * @param action
 */
export const openFormInstanceActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openFormInstanceActionItemActivatedSaga(action) {
    const formInstanceId = action.payload.itemId as string;
    yield call(openFormInstanceSaga, formInstanceId, FormOpenMethod.new);
};

/**
 * Obsluhuje výchozí chování pro akční položku otevřít v nové záložce prohlížeče ve formulářových položkách menu.
 * @param action
 */
export const openFormInTabActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openFormInTabActionItemActivatedSaga(action) {
    const formItemId = action.payload.itemId as number;
    yield call(openFormItemSaga, formItemId, FormOpenMethod.inTab);
};

/**
 * Obsluhuje výchozí chování pro akční položku otevřít v nové záložce prohlížeče v profilových položkách menu.
 * @param action
 */
export const openProfileInTabActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openProfileInTabActionItemActivatedSaga(action) {
    const profileFormRelationship: SagaYieldReturn<typeof getProfileFormRelationship> = yield select(getProfileFormRelationship, action.payload.itemId as string);
    if (profileFormRelationship) {
        yield call(openProfileSaga, profileFormRelationship.profileId, FormOpenMethod.inTab, profileFormRelationship.formItemId);
    } else {
        yield call(logger.warn, "Cannot open profile in tab because profileId for itemId %s was not found.", action.payload.itemId);
    }
};

/**
 * Obsluhuje výchozí chování pro akční položku otevřít v nové záložce prohlížeče v otevřených položkách menu.
 * @param action
 */
export const openFormInstanceInTabActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* openFormInstanceInTabActionItemActivatedSaga(action) {
    const formInstanceId = action.payload.itemId as string;
    yield call(openFormInstanceSaga, formInstanceId, FormOpenMethod.inTab);
};

/**
 * Obsluhuje výchozí chování pro akční položku zavření formuláře.
 * @param action
 */
export const closeActionItemActivatedSaga: SagaWithReturnType<[IMenuItemDefaultActionAllowedAction]> = function* closeActionItemActivatedSaga(action) {
    const formInstanceId = action.payload.itemId as string;
    yield delay(1); // Použit z důvodu "počkání" na dokončení obsluhy eventu valueChanged //TODO - 36658 - Odstranit delay ze sagy
    yield put(createCloseFormAction(formInstanceId));
};

/**
 * Otevře zadanou formulářovou položku požadovanou metodou. Před otevřením kontroluje restrikce.
 * @param formItemId Id formulářové položky k otevření.
 * @param openMethod Metoda otevření.
 */
export const openFormItemSaga: SagaWithReturnType<[number, FormOpenMethod]> = function* openFormItemSaga(formItemId, openMethod) {
    const windowActionsEnabled: SagaYieldReturn<typeof areFormItemWindowActionsEnabled> = yield select(areFormItemWindowActionsEnabled, formItemId);
    if (!windowActionsEnabled) {
        return;
    }
    const formItem: SagaYieldReturn<typeof getFormItem> = yield select(getFormItem, formItemId);
    if (!formItem) {
        yield call(logger.warn, "Cannot open form item with id %s, because there is no form item with such id", formItemId);
        return;
    }
    if (!formItem.formCode) {
        yield call(logger.warn, "Cannot open form item with id %s, because form code for this item is not defined", formItemId);
        return;
    }

    let options: OpenFormData = {
        menuItemId: formItemId,
        openMode: formItem.openMode,
        isFromMainMenu: true,

    };
    const formContext = yield call(createFormContext, formItem);
    if (formContext)
        options = { ...options, formContext };

    yield put(createOpenFormAction(formItem.formCode, openMethod, options));
};

/**
 * Otevře zadaný profil formuláře požadovanou metodou. Před otevřením kontroluje restrikce.
 * @param profileId Id profilu k otevření.
 * @param openMethod Metoda otevření.
 * @param formItemId Id položky formuláře, pro který se profil otvírá. Pokud není známá, vyhledá se automaticky podle formCode.
 */
export const openProfileSaga: SagaWithReturnType<[number, FormOpenMethod, number?]> = function* openProfileSaga(profileId, openMethod, formItemId?) {
    const windowActionsEnabled: SagaYieldReturn<typeof areProfileWindowActionsEnabled> = yield select(areProfileWindowActionsEnabled, profileId);
    if (!windowActionsEnabled) {
        return;
    }
    const formItem: IMenuItemData = formItemId ? yield select(getFormItem, formItemId) : yield select(getFormItemByProfileId, profileId);
    if (!formItem) {
        yield call(logger.warn, "Cannot open profile with id %s, because there is no form item with such id", profileId);
        return;
    }
    if (!formItem.formCode) {
        yield call(logger.warn, "Cannot open profile with id %s, because form code for this profile is not defined.", profileId);
        return;
    }

    let options: OpenFormData = {
        menuItemId: formItem.id,
        openMode: formItem.openMode,
        profileId,
        isFromMainMenu: true,
    };

    const formContext = yield call(createFormContext, formItem);
    if (formContext)
        options = { ...options, formContext };

    yield put(createOpenFormAction(formItem.formCode, openMethod, options));
};

/**
 * Otevře formulář patřící k zadané instanci otevřeného formuláře.
 * @param formInstanceId Id instance otevřeného formuláře.
 * @param openMethod Metoda otevření.
 */
export const openFormInstanceSaga: SagaWithReturnType<[string, FormOpenMethod]> = function* openFormInstanceSaga(formInstanceId, openMethod) {
    const openedFormInfo: SagaYieldReturn<typeof getOpenedFormInfo> = yield select(getOpenedFormInfo, formInstanceId);
    if (!openedFormInfo) {
        yield call(logger.warn, "Cannot open form from form instance with id %s, because there is no opened form with such id", formInstanceId);
        return;
    }

    const { formCode, profileId, menuItemId, selectedId, visibleOnlyDetail } = openedFormInfo;
    if (profileId) {// otevřená položka je profil s položkou v menu
        yield call(openProfileSaga, profileId, openMethod, menuItemId);
    } else if (menuItemId) {
        yield call(openFormItemSaga, menuItemId, openMethod);
    } else {// otevřená položka nemá položku v menu
        // TODO [Tom Rejent] Doplnit obecnou kontrolu restrikcí v rámci tasku 27250.
        yield put(createOpenFormAction(formCode, openMethod, {
            isFromMainMenu: true,
            selectedId,
            visibleOnlyDetail,
        }));
    }
};

/**
 * Zajišťuje aktualizaci stavu, který je závislý na položkách profilu.
 * @param action
 */
export const formProfileChangedSaga: SagaWithReturnType<[IFormProfileChangedAction]> = function* formProfileChangedSaga(action) {
    const { type, profile } = action.payload;
    if (type === ProfileChangeType.remove) {
        const profileFormRelationships: SagaYieldReturn<typeof getProfileFormRelationshipModel> = yield select(getProfileFormRelationshipModel);
        const affectedItems = Object.values(profileFormRelationships)
            .filter((item): item is RequiredProp<ProfileFormRelationship, "formItemId"> => item.profileId === profile.id && !!item.formItemId);
        const affectedFormItemIds = affectedItems.map((item) => item.formItemId);
        yield put(createRemoveProfileFromFormsAction(profile.id, affectedFormItemIds));
        const profileRelationShipIdsToDelete = [
            createItemIdForProfile(profile.id),
            ...affectedItems.map((item) => item.id),
        ];
        yield put(createRemoveProfileFormRelationshipsAction(profileRelationShipIdsToDelete));
    } else if (type === ProfileChangeType.add) {
        // Není jednoznačné ke které položce v menu byl profil přidán, proto se znovu načtou všechny položky menu s aktuálními profily
        yield call(loadMenuItemsSaga);
        // Po volání `loadMenuItemsSaga` není akce pro přidání vztahu profilu potřebná - je však ponechána kvůli aktualizaci vyhledávání.
        yield put(createAddProfileToRelationshipsAction(profile.id));
    } else if (type === ProfileChangeType.update) {
        const profileId = action.payload.profile.id;
        const profileFormRelationShip: SagaYieldReturn<typeof getProfileFormRelationship> = yield select(getProfileFormRelationship, profileId.toString());
        // Pokud profil při aktualizaci nemá definované vazby s položkami formulářů, znovu se načtou všechny položky menu s aktuálními profily.
        // Děje se to hlavně po nastavení "Zobrazit v menu" na existujícím profilu.
        if (!profileFormRelationShip) {
            yield call(loadMenuItemsSaga);
        }
    }
};
